/**
 * ENVIRONMENT: PRODUCTION
 * MODULE: Mobile & DTH Telecom Recharges Ledger (B2C)
 * PATH: src/routes/recharges/RechargeList.js
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Table,
  Button,
  Space,
  Row,
  Col,
  Card,
  Tag,
  Tooltip,
  Typography,
  Input,
  Select,
  message,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  MobileOutlined,
  CheckCircleFilled,
  ClockCircleFilled,
  CloseCircleFilled,
  CopyOutlined,
} from "@ant-design/icons";
import _ from "lodash";
import { parseQueryParams, stringifyQueryParams } from "utils/url";
import { formatCurrency } from "utils/common";
import api from "utils/api";
import "./RechargeList.css";

const { Text, Title } = Typography;
const { Option } = Select;

function RechargeList() {
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [records, setRecords] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const location = useLocation();
  const navigate = useNavigate();

  // Fetch Live Recharges Data from Backend API
const getData = useCallback(async (query) => {
    try {
      setIsTableLoading(true);
      const activeQuery = query || parseQueryParams(location);
      
      const response = await api.post("recharges-list", activeQuery);

      if (response && response.data) {
        const resData = response.data;
        setRecords(resData.data || resData.records || []);
        setPage(Number(resData.current_page) || 1);
        setPerPage(Number(resData.per_page) || 10);
        setTotalCount(Number(resData.total) || 0);
      }
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to synchronize telecom recharge ledger.");
      setRecords([]);
      setTotalCount(0);
    } finally {
      setIsTableLoading(false);
    }
  }, [location]);

  useEffect(() => {
    const query = parseQueryParams(location);
    if (query.status) setStatusFilter(query.status);
    if (query.keyword) setSearchTerm(query.keyword);
    getData(query);
  }, [location, getData]);

  const executeSearch = useCallback(
    (value) => {
      const trimmed = (value || "").trim();
      let query = parseQueryParams(location);
      query = { ...query, page: 1 };

      if (trimmed) query.keyword = trimmed;
      else delete query.keyword;

      navigate({ pathname: location.pathname, search: stringifyQueryParams(query) });
    },
    [location, navigate]
  );

  const debouncedSearch = useRef(
    _.debounce((val) => executeSearch(val), 450)
  ).current;

  const handleInputChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    debouncedSearch(val);
  };

  const renderRupee = (val) => {
    const num = Number(val) || 0;
    try {
      if (typeof formatCurrency === "function") return formatCurrency(num);
    } catch (e) {}
    return `₹${num.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
  };

  const copyToClipboard = (text, label) => {
    if (!text || text === "N/A") return;
    navigator.clipboard.writeText(text);
    message.success(`${label} copied to clipboard`);
  };

  const handleStatusChange = (val) => {
    setStatusFilter(val);
    let query = parseQueryParams(location);
    if (val) query = { ...query, page: 1, status: val };
    else delete query.status;
    navigate({ pathname: location.pathname, search: stringifyQueryParams(query) });
  };

  const onChangeTable = (pagination) => {
    let query = parseQueryParams(location);
    query = { ...query, page: pagination.current, per_page: pagination.pageSize };
    navigate({ pathname: location.pathname, search: stringifyQueryParams(query) });
  };

  const handleRefresh = () => {
    setSearchTerm("");
    setStatusFilter("");
    navigate({ pathname: location.pathname, search: stringifyQueryParams({ page: 1 }) });
  };

const columns = [
    {
      title: "SR",
      width: 55,
      align: "center",
      render: (_, __, index) => (
        <span className="recharge-sr">{(page - 1) * perPage + index + 1}</span>
      ),
    },
    {
      title: "Txn Ref ID / Order ID",
      width: 230,
      render: (_, record) => {
        // Fallbacks for transaction ID
        const txnId = record.txnRefId || record.order_id || record.reference_id || record.txnid || "N/A";
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                fontFamily: "monospace",
                fontWeight: 600,
                color: "#0f172a",
                background: "#f1f5f9",
                padding: "3px 8px",
                borderRadius: 6,
                fontSize: 12,
              }}
            >
              {txnId}
            </span>
            {txnId !== "N/A" && (
              <Tooltip title="Copy Transaction ID">
                <CopyOutlined
                  style={{ cursor: "pointer", color: "#0284c7", fontSize: 13 }}
                  onClick={() => copyToClipboard(txnId, "Transaction ID")}
                />
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: "Subscriber Details",
      width: 200,
      render: (_, record) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontWeight: 600, color: "#0f172a", fontSize: 13.5 }}>
            {record.username || record.customer_name || record.fullname || "Consumer"}
          </span>
          <span style={{ fontSize: 12, color: "#475569", fontFamily: "monospace" }}>
            +91 {record.user_mobile || record.mobile || record.customer_phone || "N/A"}
          </span>
        </div>
      ),
    },
    {
      title: "Operator & Circle",
      width: 200,
      render: (_, record) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontWeight: 600, color: "#334155", fontSize: 13 }}>
            {record.operator_name || record.operator || record.biller_name || "Telecom Operator"}
          </span>
          <Tag color="cyan" style={{ width: "fit-content", fontSize: 10, borderRadius: 4 }}>
            {record.circle || "Direct Telco Switch"}
          </Tag>
        </div>
      ),
    },
    {
      title: "Recharge Amount",
      width: 140,
      render: (_, record) => (
        <span style={{ fontWeight: 700, color: "#0f172a", fontSize: 13.5 }}>
          {renderRupee(record.amount || record.final_amount)}
        </span>
      ),
    },
    {
      title: "Timestamp (IST)",
      width: 170,
      render: (_, record) => {
        // Fallbacks for timestamp
        const timeVal = record.date_time || record.created_at || record.date || "N/A";
        return (
          <span style={{ fontSize: 12, color: "#64748b" }}>
            {timeVal}
          </span>
        );
      },
    },
    {
      title: "Status",
      width: 120,
      align: "center",
      render: (_, record) => {
        const status = (record.status || "").toUpperCase();
        const isSuccess = status === "SUCCESS";
        const isPending = status === "PENDING" || status === "INPROCESS";
        return (
          <Tag
            icon={isSuccess ? <CheckCircleFilled /> : isPending ? <ClockCircleFilled /> : <CloseCircleFilled />}
            color={isSuccess ? "success" : isPending ? "warning" : "error"}
            style={{ borderRadius: 6, fontWeight: 600, padding: "2px 8px" }}
          >
            {status || "SUCCESS"}
          </Tag>
        );
      },
    },
  ];

  return (
    <div className="recharge-container">
      <Card className="recharge-header-card" bordered={false}>
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col xs={24} lg={8}>
            <div className="recharge-title-wrap">
              <MobileOutlined className="header-recharge-icon" />
              <div>
                <Title level={4} className="recharge-main-heading">
                  Mobile &amp; DTH Telecom Recharges
                </Title>
                <Text type="secondary" className="recharge-subtext">
                  Prepaid packs, validity extensions, data boosters &amp; direct telco routing
                </Text>
              </div>
            </div>
          </Col>

          <Col xs={24} lg={16} className="recharge-controls-wrap">
            <Space wrap size="middle" className="controls-flex">
              <Select
                placeholder="All Statuses"
                value={statusFilter || undefined}
                onChange={handleStatusChange}
                allowClear
                className="recharge-status-select"
              >
                <Option value="success">Success</Option>
                <Option value="pending">Pending</Option>
                <Option value="failed">Failed</Option>
              </Select>

              <Input
                placeholder="Search Txn Ref ID, Mobile, Operator..."
                prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                value={searchTerm}
                onChange={handleInputChange}
                onPressEnter={(e) => executeSearch(e.target.value)}
                allowClear
                className="recharge-search-input"
              />

              <Button
                icon={<ReloadOutlined />}
                onClick={handleRefresh}
                className="recharge-refresh-btn"
              />
            </Space>
          </Col>
        </Row>
      </Card>

      <Card className="recharge-table-card" bordered={false}>
        <Table
          loading={isTableLoading}
          dataSource={records}
          columns={columns}
          onChange={onChangeTable}
          rowKey={(record) => record.id || record._id}
          pagination={{
            pageSize: perPage,
            total: totalCount,
            current: page,
            showSizeChanger: true,
            pageSizeOptions: ["10", "25", "50", "100"],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} recharges`,
          }}
          scroll={{ x: 1200 }}
          className="enterprise-recharge-table"
        />
      </Card>
    </div>
  );
}

export default RechargeList;